/**
 * Noticing a newer deploy while a tab is open.
 *
 * A single-page app never fetches its start page again as people move around,
 * so an open tab keeps running the code it first loaded, for days if the tab
 * stays open. After a deploy it shows old screens (an old sign-in page, say)
 * until a full reload. The start page is never cached and names the build's
 * main script, which is renamed on every build: when the deployed one differs
 * from the one this tab runs, another version is out: a newer one, or after a
 * rollback an older one. Either way the tab should reload to match the server.
 *
 * Turned off with VITE_NEW_VERSION_CHECK=false. Checked when the tab comes
 * back into view, at most once every VITE_NEW_VERSION_CHECK_SECONDS.
 */

const DEFAULT_CHECK_SECONDS = 60;
/** A check that takes longer than this is dropped (and tried again next time). */
const CHECK_TIMEOUT_MS = 10_000;

/** Whether the check is on (VITE_NEW_VERSION_CHECK is not "false"). */
export function newVersionCheckOn(): boolean {
  return import.meta.env.VITE_NEW_VERSION_CHECK !== "false";
}

/** The shortest gap between two checks, in milliseconds. */
function checkGapMs(): number {
  const seconds = Number(import.meta.env.VITE_NEW_VERSION_CHECK_SECONDS);
  return (Number.isFinite(seconds) && seconds >= 0 ? seconds : DEFAULT_CHECK_SECONDS) * 1000;
}

/** A built main script's path: under /assets/. */
function assetPath(src: string): string | null {
  try {
    const path = new URL(src, window.location.origin).pathname;
    return path.startsWith("/assets/") ? path : null;
  } catch {
    return null;
  }
}

/** The main script this tab is running. Null in development, where it isn't built. */
export function runningScript(): string | null {
  for (const s of Array.from(
    document.querySelectorAll<HTMLScriptElement>('script[type="module"][src]'),
  )) {
    const path = assetPath(s.src);
    if (path) return path;
  }
  return null;
}

/** The main script named in a start page's HTML. */
export function scriptInPage(html: string): string | null {
  const doc = new DOMParser().parseFromString(html, "text/html");
  for (const s of Array.from(doc.querySelectorAll('script[type="module"][src]'))) {
    const path = assetPath(s.getAttribute("src") ?? "");
    if (path) return path;
  }
  return null;
}

/** The main script deployed now. Null when it can't be told (offline, say). */
async function deployedScript(): Promise<string | null> {
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), CHECK_TIMEOUT_MS);
  try {
    const res = await fetch(`/?_v=${Date.now()}`, {
      cache: "no-store",
      credentials: "same-origin",
      signal: abort.signal,
    });
    if (!res.ok) return null;
    return scriptInPage(await res.text());
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * The deployed version's script, when it isn't the one this tab runs. Null
 * when it is, in development, or when it can't be told.
 */
export async function newerVersion(): Promise<string | null> {
  if (!newVersionCheckOn()) return null;
  const running = runningScript();
  if (!running) return null;
  const deployed = await deployedScript();
  return deployed && deployed !== running ? deployed : null;
}

/**
 * Calls `onNewVersion` once, when a newer version is deployed: checked as the
 * tab comes back into view. Returns the clean-up.
 */
export function watchForNewVersion(onNewVersion: () => void): () => void {
  if (!newVersionCheckOn() || !runningScript()) return () => undefined;
  let told = false;
  let lastCheck = 0;
  const check = async () => {
    if (told || document.visibilityState !== "visible") return;
    if (Date.now() - lastCheck < checkGapMs()) return;
    lastCheck = Date.now();
    if (await newerVersion()) {
      told = true;
      onNewVersion();
    }
  };
  document.addEventListener("visibilitychange", check);
  window.addEventListener("focus", check);
  return () => {
    document.removeEventListener("visibilitychange", check);
    window.removeEventListener("focus", check);
  };
}

/** The newer version this tab last reloaded for, so it never reloads in a loop. */
const RELOADED_FOR_KEY = "skysupport.newVersion.reloadedFor";

/**
 * Reloads into a newer version when there's one and nothing would be lost:
 * for the sign-in page, before anyone starts typing. Once per version, so a
 * start page that keeps naming another script (part-way through a deploy)
 * can't reload it again and again.
 */
export async function reloadIfNewerVersion(nothingToLose: () => boolean): Promise<boolean> {
  const newer = await newerVersion();
  if (!newer || !nothingToLose()) return false;
  try {
    if (sessionStorage.getItem(RELOADED_FOR_KEY) === newer) return false;
    sessionStorage.setItem(RELOADED_FOR_KEY, newer);
  } catch {
    // No session storage: without the guard, don't reload at all.
    return false;
  }
  window.location.reload();
  return true;
}
